import { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Trash2, Mail } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button, Input, SectionLoader } from '@/components/atoms'
import { authService } from '@/services/authService'
import { organizationService } from '@/services/organizationService'
import { Organization, OrgInvitation, OrgMembership, OrgRole } from '@/types/organization'

const ROLES: OrgRole[] = ['OWNER', 'ADMIN', 'MEMBER']

const OrganizationDetailScreen = () => {
  const { orgId = '' } = useParams<{ orgId: string }>()
  const currentUserId = authService.getUser()?.id

  const [org, setOrg] = useState<Organization | null>(null)
  const [members, setMembers] = useState<OrgMembership[]>([])
  const [invitations, setInvitations] = useState<OrgInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<OrgRole>('MEMBER')
  const [inviting, setInviting] = useState(false)

  const myRole = members.find(m => m.user_id === currentUserId)?.role
  const canManage = myRole === 'OWNER' || myRole === 'ADMIN'
  const isOwner = myRole === 'OWNER'

  const load = useCallback(async () => {
    setError(null)
    try {
      const [orgData, memberData, inviteData] = await Promise.all([
        organizationService.getOrganization(orgId),
        organizationService.listMembers(orgId),
        organizationService.listInvitations(orgId).catch(() => []),
      ])
      setOrg(orgData)
      setMembers(memberData)
      setInvitations(inviteData)
    } catch (err: any) {
      setError(err?.message || 'Failed to load organization')
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => { load() }, [load])

  const handleRoleChange = async (userId: string, role: OrgRole) => {
    try {
      await organizationService.updateMemberRole(orgId, userId, role)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Failed to update role')
    }
  }

  const handleRemove = async (userId: string) => {
    try {
      await organizationService.removeMember(orgId, userId)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Failed to remove member')
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviting(true)
    setError(null)
    try {
      await organizationService.createInvitation(orgId, inviteEmail.trim(), inviteRole)
      setInviteEmail('')
      setInviteRole('MEMBER')
      await load()
    } catch (err: any) {
      setError(err?.message || 'Failed to send invitation')
    } finally {
      setInviting(false)
    }
  }

  const handleRevoke = async (inviteId: string) => {
    try {
      await organizationService.revokeInvitation(orgId, inviteId)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke invitation')
    }
  }

  if (loading) return <SectionLoader />
  if (!org) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg">
          {error || 'Organization not found'}
        </div>
      </div>
    )
  }

  const pending = invitations.filter(i => i.status === 'PENDING')

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{org.name}</h1>
        {org.description && <p className="text-muted-foreground">{org.description}</p>}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Members */}
      <Card className="p-4">
        <h2 className="font-semibold text-foreground mb-4">Members</h2>
        <div className="space-y-2">
          {members.map(m => {
            const name = [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ')
            return (
              <div key={m.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-foreground truncate">{name || m.user?.email || m.user_id}</div>
                  {m.user?.email && <div className="text-xs text-muted-foreground truncate">{m.user.email}</div>}
                </div>
                {isOwner ? (
                  <select
                    className="bg-background border border-border rounded-md px-2 py-1 text-sm text-foreground"
                    value={m.role}
                    onChange={e => handleRoleChange(m.user_id, e.target.value as OrgRole)}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">{m.role}</span>
                )}
                {canManage && m.user_id !== currentUserId && (
                  <button
                    onClick={() => handleRemove(m.user_id)}
                    className="text-muted-foreground hover:text-red-500 p-1"
                    aria-label="Remove member"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </Card>

      {/* Invitations */}
      {canManage && (
        <Card className="p-4">
          <h2 className="font-semibold text-foreground mb-4">Invite a member</h2>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="name@company.com"
              leftElement={<Mail size={16} />}
              required
            />
            <select
              className="bg-background border border-border rounded-md px-3 text-sm text-foreground"
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value as OrgRole)}
            >
              {(isOwner ? ROLES : ROLES.filter(r => r !== 'OWNER')).map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <Button type="submit" variant="primary" disabled={inviting}>
              {inviting ? 'Sending…' : 'Invite'}
            </Button>
          </form>

          {pending.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Pending invitations</div>
              {pending.map(inv => (
                <div key={inv.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground truncate">{inv.email}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{inv.role}</div>
                  </div>
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    className="text-muted-foreground hover:text-red-500 p-1"
                    aria-label="Revoke invitation"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

export default OrganizationDetailScreen
